import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { CasinoSettings } from '@/types'
import {
  notifyDepositApproved,
  notifyDepositRejected,
  notifyWithdrawalApproved,
  notifyWithdrawalRejected,
  notifyKycApproved,
  notifyKycRejected,
  notifyWelcome,
} from '@/features/notifications'

// Types
export interface AdminStats {
  totalUsers: number
  pendingDeposits: number
  pendingWithdrawals: number
  pendingKyc: number
  totalDeposits: number
  totalWithdrawals: number
  pendingMemberships: number
}

export interface UserData {
  id: string
  email: string
  displayName: string
  balance: number
  isAdmin: boolean
  isPatriarch: boolean
  emailVerified: boolean
  kycStatus: 'none' | 'pending' | 'approved' | 'rejected'
  membershipStatus: 'pending' | 'approved' | 'rejected'
  createdAt: Date
}

export interface DepositData {
  id: string
  userId: string
  userEmail: string
  userName: string
  amount: number
  method: string
  status: 'pending' | 'approved' | 'rejected'
  reference?: string
  createdAt: Date
}

export interface WithdrawalData {
  id: string
  userId: string
  userEmail: string
  userName: string
  amount: number
  method: string
  status: 'pending' | 'approved' | 'rejected'
  accountInfo?: string
  createdAt: Date
}

export interface KycData {
  id: string
  userId: string
  userEmail?: string
  userName?: string
  fullName: string
  documentType: string
  documentNumber: string
  status: 'pending' | 'approved' | 'rejected'
  submittedAt: Date
  docFront?: string
  docBack?: string
  selfie?: string
}

export interface CodeData {
  code: string
  createdBy: string
  createdByName: string
  createdByRole: string
  active: boolean
  usedCount: number
  createdAt: Date
  isPatriarchCode: boolean
}

// Get admin stats
export async function getAdminStats(): Promise<AdminStats> {
  try {
    const [users, deposits, withdrawals, kyc] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(query(collection(db, 'deposits'), where('status', '==', 'pending'))),
      getDocs(query(collection(db, 'withdrawals'), where('status', '==', 'pending'))),
      getDocs(query(collection(db, 'kyc'), where('status', '==', 'pending'))),
    ])

    const pendingMemberships = users.docs.filter(
      (d) => d.data().membershipStatus === 'pending'
    ).length

    const allDeposits = await getDocs(
      query(collection(db, 'deposits'), where('status', '==', 'approved'))
    )
    const allWithdrawals = await getDocs(
      query(collection(db, 'withdrawals'), where('status', '==', 'approved'))
    )

    return {
      totalUsers: users.size,
      pendingDeposits: deposits.size,
      pendingWithdrawals: withdrawals.size,
      pendingKyc: kyc.size,
      pendingMemberships,
      totalDeposits: allDeposits.docs.reduce((acc, d) => acc + (d.data().amount || 0), 0),
      totalWithdrawals: allWithdrawals.docs.reduce((acc, d) => acc + (d.data().amount || 0), 0),
    }
  } catch (error) {
    console.error('Error getting admin stats:', error)
    return {
      totalUsers: 0,
      pendingDeposits: 0,
      pendingWithdrawals: 0,
      pendingKyc: 0,
      pendingMemberships: 0,
      totalDeposits: 0,
      totalWithdrawals: 0,
    }
  }
}

// Get all users
export async function getUsers(): Promise<UserData[]> {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'users'), orderBy('createdAt', 'desc'))
    )
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as UserData[]
  } catch (error) {
    console.error('Error getting users:', error)
    return []
  }
}

// Update user
export async function updateUser(
  userId: string,
  data: Partial<UserData>
): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...data,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    console.error('Error updating user:', error)
    return { success: false, error: 'Error actualizando usuario' }
  }
}

// Delete user
export async function deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Delete from Firestore
    await deleteDoc(doc(db, 'users', userId))

    // Call API to delete from Firebase Auth
    const response = await fetch('/api/delete-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    })

    if (!response.ok) {
      console.warn('Could not delete from Firebase Auth')
    }

    return { success: true }
  } catch (error) {
    console.error('Error deleting user:', error)
    return { success: false, error: 'Error eliminando usuario' }
  }
}

// Get deposits
export async function getDeposits(status?: string): Promise<DepositData[]> {
  try {
    let q = query(collection(db, 'deposits'), orderBy('createdAt', 'desc'), limit(100))
    if (status) {
      q = query(collection(db, 'deposits'), where('status', '==', status), orderBy('createdAt', 'desc'))
    }
    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as DepositData[]
  } catch (error) {
    console.error('Error getting deposits:', error)
    return []
  }
}

// Approve/Reject deposit
export async function updateDepositStatus(
  depositId: string,
  status: 'approved' | 'rejected',
  userId: string,
  amount: number
): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'deposits', depositId), {
      status,
      reviewedAt: serverTimestamp(),
    })

    // If approved, add balance to user
    if (status === 'approved') {
      const userRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userRef)
      if (userDoc.exists()) {
        const currentBalance = userDoc.data().balance || 0
        await updateDoc(userRef, {
          balance: currentBalance + amount,
        })
      }
      // Send notification
      await notifyDepositApproved(userId, amount)
    } else {
      // Send rejection notification
      await notifyDepositRejected(userId, amount)
    }

    return { success: true }
  } catch (error) {
    console.error('Error updating deposit:', error)
    return { success: false, error: 'Error actualizando depósito' }
  }
}

// Get withdrawals
export async function getWithdrawals(status?: string): Promise<WithdrawalData[]> {
  try {
    let q = query(collection(db, 'withdrawals'), orderBy('createdAt', 'desc'), limit(100))
    if (status) {
      q = query(collection(db, 'withdrawals'), where('status', '==', status), orderBy('createdAt', 'desc'))
    }
    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as WithdrawalData[]
  } catch (error) {
    console.error('Error getting withdrawals:', error)
    return []
  }
}

// Approve/Reject withdrawal
export async function updateWithdrawalStatus(
  withdrawalId: string,
  status: 'approved' | 'rejected',
  userId: string,
  amount: number
): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'withdrawals', withdrawalId), {
      status,
      reviewedAt: serverTimestamp(),
    })

    // If rejected, return balance to user
    if (status === 'rejected') {
      const userRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userRef)
      if (userDoc.exists()) {
        const currentBalance = userDoc.data().balance || 0
        await updateDoc(userRef, {
          balance: currentBalance + amount,
        })
      }
      // Send rejection notification
      await notifyWithdrawalRejected(userId, amount)
    } else {
      // Send approval notification
      await notifyWithdrawalApproved(userId, amount)
    }

    return { success: true }
  } catch (error) {
    console.error('Error updating withdrawal:', error)
    return { success: false, error: 'Error actualizando retiro' }
  }
}

// Get KYC submissions
export async function getKycSubmissions(status?: string): Promise<KycData[]> {
  try {
    let q = query(collection(db, 'kyc'), orderBy('submittedAt', 'desc'), limit(100))
    if (status) {
      q = query(collection(db, 'kyc'), where('status', '==', status), orderBy('submittedAt', 'desc'))
    }
    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      userId: doc.id,
      ...doc.data(),
      submittedAt: doc.data().submittedAt?.toDate() || new Date(),
    })) as KycData[]
  } catch (error) {
    console.error('Error getting KYC:', error)
    return []
  }
}

// Approve/Reject KYC
export async function updateKycStatus(
  kycId: string,
  status: 'approved' | 'rejected',
  rejectionReason?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const baseUpdate = {
      status,
      reviewedAt: serverTimestamp(),
    }
    const updateData = rejectionReason
      ? { ...baseUpdate, rejectionReason }
      : baseUpdate

    await updateDoc(doc(db, 'kyc', kycId), updateData)

    // Update user's KYC status
    await updateDoc(doc(db, 'users', kycId), {
      kycStatus: status,
    })

    // Send notification
    if (status === 'approved') {
      await notifyKycApproved(kycId)
    } else {
      await notifyKycRejected(kycId, rejectionReason)
    }

    return { success: true }
  } catch (error) {
    console.error('Error updating KYC:', error)
    return { success: false, error: 'Error actualizando KYC' }
  }
}

// Get referral codes
export async function getCodes(): Promise<CodeData[]> {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'referralCodes'), orderBy('createdAt', 'desc'))
    )
    return snapshot.docs.map((doc) => ({
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as CodeData[]
  } catch (error) {
    console.error('Error getting codes:', error)
    return []
  }
}

// Delete code
export async function deleteCode(code: string): Promise<{ success: boolean; error?: string }> {
  try {
    await deleteDoc(doc(db, 'referralCodes', code))
    return { success: true }
  } catch (error) {
    console.error('Error deleting code:', error)
    return { success: false, error: 'Error eliminando código' }
  }
}

// Create patriarch code
export async function createPatriarchCode(
  email: string,
  adminId: string,
  adminName: string
): Promise<{ success: boolean; code?: string; error?: string }> {
  try {
    const code = `PATRIARCH-${Math.random().toString(36).substring(2, 8).toUpperCase()}`

    const { setDoc } = await import('firebase/firestore')
    await setDoc(doc(db, 'referralCodes', code), {
      code,
      createdBy: adminId,
      createdByName: adminName,
      createdByRole: 'admin',
      createdAt: serverTimestamp(),
      active: true,
      usedCount: 0,
      maxUses: 1,
      usedBy: [],
      isPatriarchCode: true,
      targetEmail: email,
    })

    // Send email with code (call API)
    await fetch('/api/send-verification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'patriarch-invite',
        email,
        code,
      }),
    })

    return { success: true, code }
  } catch (error) {
    console.error('Error creating patriarch code:', error)
    return { success: false, error: 'Error creando código' }
  }
}

// Approve membership
export async function approveMembership(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Get user name for welcome notification
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    const userName = userDoc.exists() ? (userDoc.data().displayName || 'Jugador') : 'Jugador'

    await updateDoc(userRef, {
      membershipStatus: 'approved',
      approvedAt: serverTimestamp(),
    })

    // Send welcome notification
    await notifyWelcome(userId, userName)

    return { success: true }
  } catch (error) {
    console.error('Error approving membership:', error)
    return { success: false, error: 'Error aprobando membresía' }
  }
}

// Reject membership
export async function rejectMembership(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      membershipStatus: 'rejected',
      rejectedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    console.error('Error rejecting membership:', error)
    return { success: false, error: 'Error rechazando membresía' }
  }
}

// Format currency
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  }).format(amount)
}

// Format date
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

// ============================================
// CASINO SETTINGS (Fase 11)
// ============================================

// Default settings
export function getDefaultSettings(): CasinoSettings {
  return {
    bankAccount: {
      cbu: '',
      alias: '',
      banco: '',
      titular: '',
      cuit: '',
    },
    mercadoPagoAccount: {
      cvu: '',
      alias: '',
      titular: '',
    },
    mercadoPagoApi: {
      accessToken: '',
      publicKey: '',
      enabled: false,
    },
    limits: {
      minDeposit: 1000,
      maxDeposit: 100000,
      minWithdrawal: 2000,
      maxDailyWithdrawal: 50000,
    },
    general: {
      demoBalance: 10000,
      minBetRuleta: 50,
      minBetBlackjack: 100,
      minBetBaccarat: 100,
      maintenanceMode: false,
    },
  }
}

// Get casino settings from Firestore
export async function getCasinoSettings(): Promise<CasinoSettings> {
  try {
    const docRef = doc(db, 'config', 'casino')
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return {
        bankAccount: data.bankAccount || getDefaultSettings().bankAccount,
        mercadoPagoAccount: data.mercadoPagoAccount || getDefaultSettings().mercadoPagoAccount,
        mercadoPagoApi: data.mercadoPagoApi || getDefaultSettings().mercadoPagoApi,
        limits: data.limits || getDefaultSettings().limits,
        general: data.general || getDefaultSettings().general,
        updatedAt: data.updatedAt?.toDate(),
        updatedBy: data.updatedBy,
      }
    }

    return getDefaultSettings()
  } catch (error) {
    console.error('Error getting casino settings:', error)
    return getDefaultSettings()
  }
}

// Update casino settings
export async function updateCasinoSettings(
  settings: Partial<CasinoSettings>,
  adminId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const docRef = doc(db, 'config', 'casino')

    await setDoc(docRef, {
      ...settings,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
    }, { merge: true })

    return { success: true }
  } catch (error) {
    console.error('Error updating casino settings:', error)
    return { success: false, error: 'Error guardando configuración' }
  }
}

// Update specific section of settings
export async function updateSettingsSection(
  section: keyof CasinoSettings,
  data: CasinoSettings[keyof CasinoSettings],
  adminId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const docRef = doc(db, 'config', 'casino')

    await setDoc(docRef, {
      [section]: data,
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
    }, { merge: true })

    return { success: true }
  } catch (error) {
    console.error('Error updating settings section:', error)
    return { success: false, error: 'Error guardando sección' }
  }
}
